import type { Client, Conversation, Message, Participant } from '@twilio/conversations';
import type { Dispatch } from 'redux';
import {
  ACTION_ADD_MESSAGE,
  ACTION_REMOVE_MESSAGE,
  ACTION_UPDATE_MESSAGE,
  ACTION_UPDATE_PARTICIPANT,
  ACTION_ADD_PARTICIPANT,
  ACTION_REMOVE_PARTICIPANT,
  ACTION_UPDATE_SESSION_DATA,
  ACTION_UPDATE_CONVERSATION_STATE,
  ACTION_UPDATE_CONNECTION_STATE,
  ACTION_ADD_PARTICIPANT_EVENT_MESSAGE,
} from './actionTypes';
import { externalEventService } from '../../services/externalEventService';
import { addTwilioMessage } from '../../redux/actions/chatActions'
import store from '../../redux/store';

const processedParticipantJoins = new Set<string>();
const activeAgentSids = new Set<string>();
const pendingLeaveTimers = new Map<string, NodeJS.Timeout>();
let disconnectMessageShown = false;

const isRealAgent = (identity?: string) => {
  if (!identity) return false;

  const decoded = identity
    .replace(/_40/g, '@')
    .replace(/_2E/g, '.');

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(decoded);
};
// Helper function to extract first name from email or encoded email
const extractFirstName = (fullName: string): string => {
  if (!fullName || fullName === 'Unknown User') {
    return fullName;
  }

  // Handle encoded email format (e.g., "gita_2Egustianingsih_40sportserve_2Eco")
  if (fullName.includes('_2E') || fullName.includes('_40')) {
    // Decode the email: _2E = . and _40 = @
    const decodedEmail = fullName.replace(/_2E/g, '.').replace(/_40/g, '@');
    // Extract first name from decoded email
    const firstPart = decodedEmail.split('@')[0];
    return firstPart.split('.')[0];
  }

  // Handle regular email format (e.g., "gita.Egustianingsih@sportserve.co")
  if (fullName.includes('@')) {
    const firstPart = fullName.split('@')[0];
    return firstPart.split('.')[0];
  }

  // If it's already just a name, return as is
  return fullName;
};

const decodeTwilioIdentity = (value?: string) => {
  if (!value) return value;

  return value.replace(/_([0-9A-F]{2})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );
};

const isTwilioStyleIdentity = (value?: string): boolean => {
  return !!value && /^[A-Z]{2}[a-fA-F0-9]{32}$/.test(value);
};

const isEncodedEmailIdentity = (value?: string): boolean => {
  return !!value && /_40|_2E/.test(value);
};

const getMessageDirection = (
  author: string | null,
  currentUserIdentity?: string | null
): { isSent: boolean; isReceived: boolean; direction: 'sent' | 'received' } => {
  if (isTwilioStyleIdentity(author || undefined)) {
    return { isSent: true, isReceived: false, direction: 'sent' };
  }

  if (isEncodedEmailIdentity(author || undefined)) {
    return { isSent: false, isReceived: true, direction: 'received' };
  }

  const isSent = author === currentUserIdentity;
  const isReceived = !isSent;

  return { isSent, isReceived, direction: isReceived ? 'received' : 'sent' };
};

// Helper function to get participant name from message author with user object
const getParticipantNameFromAuthor = (messageAuthor: string | null, user?: any): string => {
  // Use user?.friendlyName first, then messageAuthor, then fallback
  const rawName = user?.friendlyName || messageAuthor || 'Unknown User';

  // Use the existing extractFirstName function for consistent name extraction
  let participantName = extractFirstName(rawName);

  // Apply the same fallback logic as in createParticipantEventMessage
  if (participantName === 'Unknown User') {
    participantName = "agent";
  }

  return participantName;
};

// Helper function to create participant event messages
export const createParticipantEventMessage = async (
  participant: Participant,
  action: 'joined' | 'left',
  user?: any,
  conversationSid?: string
) => {
  console.log(' PARTICIPANT EVENT MESSAGE:', {
    participantSid: participant.sid,
    identity: (participant as any),
    userFriendlyName: user?.friendlyName,
    userIdentity: user,
    action: action
  });

const decodedUserIdentity = decodeTwilioIdentity(user?.identity);
const decodedParticipantIdentity = decodeTwilioIdentity((participant as any)?.identity);
  const browserLanguage = navigator.language;
            const response = await fetch("https://api.ipify.org?format=json");
            const { ip } = await response.json();

            console.log(ip);

  // Create comprehensive event data for processEvent
  const eventData = {
  participantSid: participant.sid,
  identity: decodedParticipantIdentity,
  type: (participant as any)?.type,
  userFriendlyName: user?.friendlyName,
  userIdentity: decodedUserIdentity, 
  conversationSid: conversationSid,
  timestamp: new Date().toISOString(),
  action: action,
  browserLanguage: browserLanguage,
  ipAddress: ip,
  closed: 'chat_client'

  };

  // Call processEvent based on action
  if (conversationSid) {
    try {
      const eventType = action === 'joined' ? 'PARTICIPANT_ADDED' : 'PARTICIPANT_DISCONNECTED';
      await externalEventService.processEvent(eventType, eventData, conversationSid);
      console.log(`✅ PROCESSED ${eventType} EVENT FROM createParticipantEventMessage`);
    } catch (error) {
      console.error(`❌ FAILED TO PROCESS ${action} EVENT FROM createParticipantEventMessage:`, error);
    }
  }

  const rawName = user?.friendlyName || (participant as any)?.identity || 'Unknown User';

  const participantType = (participant as any)?.type;
  const identity = user?.identity || (participant as any)?.identity;

  if (participantType === 'dialogflowcx') {
    return null;
  }
  const decodedIdentity = identity
  ?.replace(/_40/g, '@')
  ?.replace(/_2E/g, '.');
  
  const isRealAgent =
  decodedIdentity &&
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(decodedIdentity);

  if (!isRealAgent) {
    return null;
  }

  // Use the existing extractFirstName function for consistent name extraction
  let participantName = extractFirstName(rawName);

  console.log('👤 Participant name extraction:', {
    rawName,
    extractedName: participantName
  });

  // Skip cleanup event
  if (action === 'left' && participantName === 'Unknown User') return null;

  // Read config safely
  const state = store.getState();
  const formData = state.config?.config?.data?.[0]?.attributes;

  // Provide fallback messages if config not ready
  const joinMessageTemplate = formData?.ChatBox?.JoinChatMessage || 'has joined the chatgroup';
  const leaveMessageTemplate = formData?.ChatBox?.LeaveChatMessage || 'has left the chatgroup';

  // Format current date and time
  const now = new Date();
  const dateTimeString = now.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });

if (participantName === 'Unknown User') {
  participantName = "agent";
}

const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const checkRawName = (/_2E|_40/.test(rawName) || emailPattern.test(rawName))
console.log("checkRawName-----", checkRawName)

  const eventText =
    action === 'joined'
      ? `${participantName} ${joinMessageTemplate} - (${dateTimeString})`
      : `${participantName} ${leaveMessageTemplate} - (${dateTimeString})`;

  console.log('🧩 Generated participant event:', { eventText, formData });
  return {
    sid: `event_${participant.sid}_${action}_${Date.now()}`,
    index: Date.now(),
    author: participantName,
    body: eventText,
    dateCreated: new Date(),
    type: 'participant_event',
    participantSid: participant.sid,
    participantIdentity: (participant as any)?.identity,
    participantType: (participant as any)?.type,
    eventType: action,
    isSystemMessage: true,
  };
};

let _restartButtonShown = false;

export const resetRestartButtonShown = () => {
  _restartButtonShown = false;
};

export const initClientListeners = (conversationClient: Client, dispatch: Dispatch, getUpdatedToken?: () => Promise<{ token: string, conversationSid: string } | null>, conversation?: Conversation) => {
  const tokenAboutToExpireEvent = 'tokenAboutToExpire';
  const connectionStateChangedEvent = 'connectionStateChanged';

  conversationClient.removeAllListeners(tokenAboutToExpireEvent);
  conversationClient.addListener(tokenAboutToExpireEvent, async () => {
    const data = getUpdatedToken ? await getUpdatedToken() : null;
    if (data?.token && data?.conversationSid) {
      await conversationClient.updateToken(data.token);
      dispatch({
        type: ACTION_UPDATE_SESSION_DATA,
        payload: { token: data.token, conversationSid: data.conversationSid },
      });
    }
  });

  let connectionLossTimer: ReturnType<typeof setTimeout> | null = null;
  const CONNECTION_LOSS_DELAY = 30_000; // 30 seconds

  const startConnectionLossTimer = (source: string) => {
    if (_restartButtonShown) return; // Already showing restart — no need to start another timer
    if (!connectionLossTimer) {
      console.log(`⏳ Connection lost (${source}) — waiting 30s before showing restart button`);
      connectionLossTimer = setTimeout(async () => {
        connectionLossTimer = null;
        _restartButtonShown = true;
        console.log(`❌ Connection still lost after 30s (${source}) — leaving conversation and showing restart button`);

        // Leave the conversation so the server knows the user is gone
        if (conversation) {
          try {
            await conversation.leave();
            console.log('👋 Successfully left the conversation after connection loss');
          } catch (err) {
            console.error('⚠️ Failed to leave conversation after connection loss:', err);
          }
        }

        dispatch({ type: ACTION_UPDATE_CONNECTION_STATE, payload: { connectionState: 'retrying' } });
      }, CONNECTION_LOSS_DELAY);
    }
  };

  const cancelConnectionLossTimer = (source: string) => {
    if (connectionLossTimer) {
      clearTimeout(connectionLossTimer);
      connectionLossTimer = null;
      console.log(`✅ Connection restored (${source}) within 30s — resuming normally`);
    }
    // Only dispatch 'connected' if the restart button hasn't been shown yet
    if (!_restartButtonShown) {
      dispatch({ type: ACTION_UPDATE_CONNECTION_STATE, payload: { connectionState: 'connected' } });
    } else {
      console.log(`🔒 Restart button already shown — keeping it visible despite reconnection (${source})`);
    }
  };

  // --- Twilio SDK connection state listener ---
  conversationClient.removeAllListeners(connectionStateChangedEvent);
  conversationClient.addListener(connectionStateChangedEvent, (connectionStatus: string) => {
    console.log('🔌 Twilio connection status changed:', connectionStatus);

    if (connectionStatus === 'connected') {
      cancelConnectionLossTimer('twilio-sdk');
    } else if (connectionStatus === 'disconnected' || connectionStatus === 'retrying') {
      startConnectionLossTimer('twilio-sdk');
    } else {
      // Other states (connecting, denied, etc.) — forward as-is
      dispatch({ type: ACTION_UPDATE_CONNECTION_STATE, payload: { connectionState: connectionStatus } });
    }
  });

  // --- Browser native offline/online listeners (fires immediately on WiFi off) ---
  const handleOffline = () => {
    console.log('🔌 Browser went offline (WiFi / network dropped)');
    startConnectionLossTimer('browser-offline');
  };

  const handleOnline = () => {
    console.log('🔌 Browser back online (WiFi / network restored)');
    cancelConnectionLossTimer('browser-online');
  };

  window.removeEventListener('offline', handleOffline);
  window.removeEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);

  // If browser is already offline at the time listeners are set up, start the timer now
  if (!navigator.onLine) {
    console.log('🔌 Browser is already offline at listener init');
    startConnectionLossTimer('browser-already-offline');
  }
};

export const initConversationListener = (conversation: Conversation, dispatch: Dispatch) => {
  conversation.addListener('updated', async ({ conversation: updatedConversation, updateReasons }) => {
    if (updateReasons?.includes('state')) {
      const raw = (updatedConversation as any)?.state;
      const state: string = typeof raw === 'object' && raw?.current ? raw.current : (raw as string) || 'active';

      console.log('🔄 CONVERSATION STATE CHANGED:', {
        previousState: 'unknown', // We don't track previous state currently
        newState: state,
        conversationSid: conversation.sid,
        timestamp: new Date().toISOString()
      });

      // If conversation state is not "active", it means chat has ended
      if (state !== "active") {
        console.log('🚪 CHAT ENDED - SENDING PARTICIPANT_DISCONNECTED EVENT:', {
          state,
          conversationSid: conversation.sid
        });

        try {
          // Get current participants to send disconnect events
          const participants = await conversation.getParticipants();

          // Send PARTICIPANT_DISCONNECTED event for each participant
          for (const participant of participants) {
            let user: any = null;
            try {
              // Get user information similar to participant listeners
              if ((participant as any)?.type === 'chat') {
                user = await (participant as any).getUser();
              } else {
                user = { identity: (participant as any)?.identity } as any;
              }
            } catch {
              user = { identity: (participant as any)?.identity } as any;
            }
            const decodedParticipantIdentity = decodeTwilioIdentity(
                        (participant as any)?.identity
                      );

            const decodedUserIdentity = decodeTwilioIdentity(user?.identity);
          

            const eventData = {
              participantSid: participant.sid,
              identity: decodedParticipantIdentity,
              type: (participant as any)?.type,
              userFriendlyName: user?.friendlyName,
              userIdentity: decodedUserIdentity,
              conversationSid: conversation.sid,
              timestamp: new Date().toISOString(),
              action: 'disconnected',
              reason: 'chat_ended',
              conversationState: state,
              closed: 'chat_client'

            };

            await externalEventService.processEvent('PARTICIPANT_DISCONNECTED', eventData, conversation.sid);
            console.log(`✅ SENT PARTICIPANT_DISCONNECTED EVENT FOR:`, {
              participantSid: participant.sid,
              identity: (participant as any)?.identity,
              conversationState: state
            });
          }
        } catch (error) {
          console.error('❌ FAILED TO SEND PARTICIPANT_DISCONNECTED EVENTS ON CHAT END:', error);
        }
      }

      dispatch({ type: ACTION_UPDATE_CONVERSATION_STATE, payload: { conversationState: state } });
    }
  });
};

export const initMessagesListener = async (conversation: Conversation, dispatch: Dispatch) => {
  // Debug: Log conversation details and participants
  console.log('🔍 INITIALIZING MESSAGE LISTENERS FOR CONVERSATION:', conversation.sid);
  try {
    const participants = await conversation.getParticipants();
    console.log('👥 CONVERSATION PARTICIPANTS:', participants.map((p: any) => ({
      identity: p.identity,
      type: p.type,
      sid: p.sid
    })));
  } catch (error) {
    console.error('❌ ERROR GETTING PARTICIPANTS:', error);
  }

  // Add a test function to simulate received messages (for debugging)
  (window as any).testReceivedMessage = async () => {
    try {
      const participants = await conversation.getParticipants();
      console.log('🧪 TEST: All participants:', participants.map((p: any) => ({
        identity: p.identity,
        type: p.type,
        sid: p.sid
      })));

      
      // Simulate a message from a different participant
      const testMessage = {
        sid: 'test-message-' + Date.now(),
        author: 'test-customer', // Different from current user
        body: 'This is a test received message',
        dateCreated: new Date(),
        type: 'text',
        attachedMedia: []
      };

      console.log('🧪 SIMULATED MESSAGE:', testMessage);

    } catch (error) {
      console.error('❌ TEST ERROR:', error);
    }
  };
  conversation.addListener('messageAdded', async (message: Message) => {
    console.log('📨 CHAT MESSAGE ADDED:', {
      messageId: message.sid,
      author: message.author,
      body: message.body,
      timestamp: message.dateCreated,
      type: message.type
    });

    // Send event to external API for both sent and received messages
    try {
      // Determine if message is sent or received
      // Get current user identity from conversation participants
      const participants = await conversation.getParticipants();
      const currentUserParticipant = participants.find((p: any) => p.type === 'chat');
      const currentUserIdentity = currentUserParticipant?.identity;

      const { isReceived, isSent, direction } = getMessageDirection(
        message.author,
        currentUserIdentity
      );

      // Get user object for the message author (similar to participant listeners)
      let messageAuthorUser: any = null;
      try {
        // Find the participant that matches the message author
        const authorParticipant = participants.find((p: any) => p.identity === message.author);
        if (authorParticipant && (authorParticipant as any)?.type === 'chat') {
          messageAuthorUser = await (authorParticipant as any).getUser();
        } else if (authorParticipant) {
          messageAuthorUser = { identity: (authorParticipant as any)?.identity } as any;
        }
      } catch {
        messageAuthorUser = { identity: message.author } as any;
      }

      // Add specific alert for received messages
      if (isReceived) {
        console.log('🔔 THIS IS A RECEIVED MESSAGE!', {
          messageAuthor: message.author,
          currentUser: currentUserIdentity,
          isReceived: isReceived,
          messageAuthorUser: messageAuthorUser
        });
      } else {
        console.log('📤 THIS IS A SENT MESSAGE!', {
          messageAuthor: message.author,
          currentUser: currentUserIdentity,
          isSent: isSent,
          messageAuthorUser: messageAuthorUser
        });
      }


      const eventData = {
        messageId: message.sid,
        author: getParticipantNameFromAuthor(message.author, messageAuthorUser),
        body: message.body,
        timestamp: message.dateCreated,
        type: message.type,
        conversationSid: conversation.sid,
        hasMedia: (message.attachedMedia?.length || 0) > 0,
        mediaCount: message.attachedMedia?.length || 0,
        direction,
        isReceived: isReceived,
        isSent: isSent,
        action: 'added',
        currentUserIdentity: currentUserIdentity
      };

      console.log(`📨 MESSAGE ${isReceived ? 'RECEIVED' : 'SENT'} - SENDING TO EXTERNAL API:`, eventData);

      await externalEventService.processEvent('MESSAGES', eventData, conversation.sid);
    } catch (error) {
      console.error('❌ FAILED TO SEND MESSAGE EVENT TO EXTERNAL API:', error);
    }

    dispatch({ type: ACTION_ADD_MESSAGE, payload: { message } });
    dispatch(addTwilioMessage(message));
  });

  conversation.addListener('messageRemoved', async (message: Message) => {
    console.log('🗑️ CHAT MESSAGE REMOVED:', {
      messageId: message.sid,
      author: message.author,
      body: message.body,
      timestamp: message.dateCreated
    });

    // Send event to external API for message removal
    try {
      // Determine if message was sent or received
      const participants = await conversation.getParticipants();
      const currentUserParticipant = participants.find((p: any) => p.type === 'chat');
      const currentUserIdentity = currentUserParticipant?.identity;

      const { isReceived, isSent, direction } = getMessageDirection(
        message.author,
        currentUserIdentity
      );

      // Get user object for the message author (similar to participant listeners)
      let messageAuthorUser: any = null;
      try {
        // Find the participant that matches the message author
        const authorParticipant = participants.find((p: any) => p.identity === message.author);
        if (authorParticipant && (authorParticipant as any)?.type === 'chat') {
          messageAuthorUser = await (authorParticipant as any).getUser();
        } else if (authorParticipant) {
          messageAuthorUser = { identity: (authorParticipant as any)?.identity } as any;
        }
      } catch {
        messageAuthorUser = { identity: message.author } as any;
      }

      const eventData = {
        messageId: message.sid,
        author: getParticipantNameFromAuthor(message.author, messageAuthorUser),
        body: message.body,
        timestamp: message.dateCreated,
        conversationSid: conversation.sid,
        direction,
        isReceived: isReceived,
        isSent: isSent,
        action: 'removed',
        currentUserIdentity: currentUserIdentity
      };

      console.log(`🗑️ MESSAGE ${isReceived ? 'RECEIVED' : 'SENT'} REMOVED - SENDING TO EXTERNAL API:`, eventData);

      await externalEventService.processEvent('MESSAGES', eventData, conversation.sid);
    } catch (error) {
      console.error('❌ FAILED TO SEND MESSAGE REMOVED EVENT TO EXTERNAL API:', error);
    }

    dispatch({ type: ACTION_REMOVE_MESSAGE, payload: { message } });
  });

  conversation.addListener('messageUpdated', async ({ message }) => {
    console.log('✏️ CHAT MESSAGE UPDATED:', {
      messageId: message.sid,
      author: message.author,
      body: message.body,
      timestamp: message.dateCreated,
      type: message.type
    });

    // Send event to external API for message update
    try {
      // Determine if message was sent or received
      const participants = await conversation.getParticipants();
      const currentUserParticipant = participants.find((p: any) => p.type === 'chat');
      const currentUserIdentity = currentUserParticipant?.identity;

      const { isReceived, isSent, direction } = getMessageDirection(
        message.author,
        currentUserIdentity
      );

      // Get user object for the message author (similar to participant listeners)
      let messageAuthorUser: any = null;
      try {
        // Find the participant that matches the message author
        const authorParticipant = participants.find((p: any) => p.identity === message.author);
        if (authorParticipant && (authorParticipant as any)?.type === 'chat') {
          messageAuthorUser = await (authorParticipant as any).getUser();
        } else if (authorParticipant) {
          messageAuthorUser = { identity: (authorParticipant as any)?.identity } as any;
        }
      } catch {
        messageAuthorUser = { identity: message.author } as any;
      }

      const eventData = {
        messageId: message.sid,
        author: getParticipantNameFromAuthor(message.author, messageAuthorUser),
        body: message.body,
        timestamp: message.dateCreated,
        type: message.type,
        conversationSid: conversation.sid,
        hasMedia: (message.attachedMedia?.length || 0) > 0,
        mediaCount: message.attachedMedia?.length || 0,
        direction,
        isReceived: isReceived,
        isSent: isSent,
        action: 'updated',
        currentUserIdentity: currentUserIdentity
      };

      console.log(`✏️ MESSAGE ${isReceived ? 'RECEIVED' : 'SENT'} UPDATED - SENDING TO EXTERNAL API:`, eventData);

      await externalEventService.processEvent('MESSAGES', eventData, conversation.sid);
    } catch (error) {
      console.error('❌ FAILED TO SEND MESSAGE UPDATED EVENT TO EXTERNAL API:', error);
    }

    dispatch({ type: ACTION_UPDATE_MESSAGE, payload: { message } });
  });
};

export const initParticipantsListener = (conversation: Conversation, dispatch: Dispatch, onRealAgentJoined?: () => void) => {
  conversation.addListener('participantJoined', async (participant: Participant) => {
    let user: any = null;
    if (processedParticipantJoins.has(participant.sid)) {
      console.log('⚠️ Duplicate participantJoined ignored for', participant.sid);
    return;
    }

    processedParticipantJoins.add(participant.sid);
    try {
      // Only chat participants support getUser(); others (e.g., dialogflowcx) will throw
      // @ts-ignore access participant.type
      if ((participant as any)?.type === 'chat') {
        user = await (participant as any).getUser();
      } else {
        user = { identity: (participant as any)?.identity } as any;
      }
    } catch {
      user = { identity: (participant as any)?.identity } as any;
    }
    const identity = user?.identity || (participant as any)?.identity;

    if (isRealAgent(identity)) {
      activeAgentSids.add(participant.sid);

      pendingLeaveTimers.forEach((timer) => clearTimeout(timer));
      disconnectMessageShown = false;
      console.log('✅ Agent joined — cleared pending disconnect timers');
    }
    console.log('👤 PARTICIPANT JOINED:', {
      participantSid: participant.sid,
      identity: (participant as any)?.identity,
      type: (participant as any)?.type,
      userFriendlyName: user?.friendlyName,
      userIdentity: user?.identity,
      timestamp: new Date().toISOString()
    });

    if (
      user?.friendlyName &&
      user?.identity &&
      user?.identity !== 'Participant'
    ) {
      console.log('🛑 REAL AGENT JOINED — stopping on-hold messages');
      onRealAgentJoined?.();
    }

    dispatch({ type: ACTION_ADD_PARTICIPANT, payload: { participant, user } });

    // Create and dispatch participant joined event message (only if not null)
    const eventMessage = await createParticipantEventMessage(participant, 'joined', user, conversation.sid);
    if (eventMessage) {
      dispatch({ type: ACTION_ADD_PARTICIPANT_EVENT_MESSAGE, payload: { eventMessage } });
    }
  });

  conversation.addListener('participantLeft', async (participant: Participant) => {
    // Get user information for proper name extraction
    let user: any = null;
    try {
      // Only chat participants support getUser(); others (e.g., dialogflowcx) will throw
      // @ts-ignore access participant.type
      if ((participant as any)?.type === 'chat') {
        user = await (participant as any).getUser();
      } else {
        user = { identity: (participant as any)?.identity } as any;
      }
    } catch {
      user = { identity: (participant as any)?.identity } as any;
    }

    console.log('👋 PARTICIPANT LEFT:', {
      participantSid: participant.sid,
      identity: (participant as any)?.identity,
      type: (participant as any)?.type,
      userFriendlyName: user?.friendlyName,
      userIdentity: user?.identity,
      timestamp: new Date().toISOString()
    });

    dispatch({ type: ACTION_REMOVE_PARTICIPANT, payload: { participant } });

    const identity = user?.identity || (participant as any)?.identity;

    if (isRealAgent(identity)) {
      activeAgentSids.delete(participant.sid);
      const hadNoAgentsAtLeave = activeAgentSids.size === 0;

      const timer = setTimeout(async () => {
        if (hadNoAgentsAtLeave && !disconnectMessageShown) {
          console.log('⚠️ SHOWING DISCONNECT MESSAGE (once)');

          disconnectMessageShown = true;

          const eventMessage = await createParticipantEventMessage(
            participant,
            'left',
            user,
            conversation.sid
          );

          if (eventMessage) {
            dispatch({
              type: ACTION_ADD_PARTICIPANT_EVENT_MESSAGE,
              payload: { eventMessage }
            });
          }
        }

        pendingLeaveTimers.delete(participant.sid);
      }, 1000);

      pendingLeaveTimers.set(participant.sid, timer);

      return; 
    }
      const eventMessage = await createParticipantEventMessage(
        participant,
        'left',
        user,
        conversation.sid
      );

      if (eventMessage) {
        dispatch({
          type: ACTION_ADD_PARTICIPANT_EVENT_MESSAGE,
          payload: { eventMessage }
        });
      }
  });

  const dispatchParticipantUpdate = (participant: Participant) => {
    dispatch({ type: ACTION_UPDATE_PARTICIPANT, payload: { participant } });
  };
  conversation.addListener('participantUpdated', ({ participant }) => dispatchParticipantUpdate(participant));
  conversation.addListener('typingStarted', dispatchParticipantUpdate);
  conversation.addListener('typingEnded', dispatchParticipantUpdate);
};

